/**
 * AddSiteEntryWizard
 * 5-step wizard to create a site database entry from scratch.
 * Creates DesignProject + SiteDatabase + PaymentMilestones + team records in one call.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import ResponsiveModal from '../common/ResponsiveModal';
import { siteDatabaseAPI, designProjectAPI, usersAPI } from '../../services/api';

const TOTAL_STEPS = 5;

const PROPERTY_TYPES = [
  { value: 'apartment', label: 'Apartment' },
  { value: 'condo', label: 'Condo' },
  { value: 'hdb', label: 'HDB' },
  { value: 'landed_single', label: 'Landed Single Storey' },
  { value: 'landed_double', label: 'Landed Double Storey' },
  { value: 'landed_triple', label: 'Landed Triple Storey' },
  { value: 'terrace_single', label: 'Terrace Single Storey' },
  { value: 'terrace_double', label: 'Terrace Double Storey' },
  { value: 'terrace_triple', label: 'Terrace Triple Storey' },
  { value: 'semi_d_single', label: 'Semi-D Single Storey' },
  { value: 'semi_d_double', label: 'Semi-D Double Storey' },
  { value: 'semi_d_triple', label: 'Semi-D Triple Storey' },
  { value: 'bungalow', label: 'Bungalow' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'other', label: 'Other' },
];

function parsePropertySizeText(text) {
  if (!text) return null;
  const match = String(text).trim().match(/^(\d+(?:\.\d+)?)\s*[xX×]\s*(\d+(?:\.\d+)?)$/);
  if (match) return parseFloat(match[1]) * parseFloat(match[2]);
  return null;
}

function formatNumber(val) {
  if (!val && val !== 0) return '';
  return Number(val).toLocaleString();
}

const emptyForm = {
  // Step 1
  projectNumber: '',

  depositAmount: '',
  depositReceivedDate: '',
  // Step 2
  clientName: '',
  clientIC: '',
  clientPhone: '',
  clientEmail: '',
  // Step 3
  addressLine1: '',
  addressLine2: '',
  postcode: '',
  city: '',
  state: '',
  propertyType: '',
  propertySizeText: '',
  // Step 4
  finalPackagePrice: '',
  commissionAmount: '',
  contractSignedDate: '',
  signingVenue: '',
  lockPricePaymentAmount: '',
  lockPricePaymentDate: '',
  lockPricePaymentMethod: '',
  lockPriceReferenceNumber: '',
  // Step 5
  managerId: '',
  leaderId: '',
  agentId: '',
  coClosingAgents: [],
  sidId: '',
  siteManagerId: '',
};

export default function AddSiteEntryWizard({ isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [projectNumberSuggestion, setProjectNumberSuggestion] = useState('');
  const [lockPriceOpen, setLockPriceOpen] = useState(false);

  // Dropdown data
  const [managers, setManagers] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sidUsers, setSidUsers] = useState([]);
  const [siteManagers, setSiteManagers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Fetch suggestion and users on open
  useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    setForm(emptyForm);
    setErrors({});
    setLockPriceOpen(false);

    designProjectAPI.getNextProjectNumber()
      .then(res => setProjectNumberSuggestion(res.data?.nextProjectNumber || ''))
      .catch(() => {});

    setLoadingUsers(true);
    Promise.all([
      usersAPI.getByRoles(['manager']),
      usersAPI.getByRoles(['leader']),
      usersAPI.getByRoles(['agent']),
      usersAPI.getByRoles(['sid', 'asid']),
      siteDatabaseAPI.getAvailableSiteManagers(),
    ]).then(([mgr, ldr, agt, sid, sm]) => {
      setManagers(mgr.data?.data || []);
      setLeaders(ldr.data?.data || []);
      setAgents(agt.data?.data || []);
      setSidUsers(sid.data?.data || []);
      setSiteManagers(sm.data?.data || []);
    }).catch(() => {
      toast.error('Failed to load user lists');
    }).finally(() => setLoadingUsers(false));
  }, [isOpen]);

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };


  // Validate current step
  const validateStep = useCallback((s) => {
    const errs = {};
    if (s === 1) {
      if (!form.projectNumber.trim()) errs.projectNumber = 'Project number is required';
    }
    if (s === 2) {
      if (!form.clientName.trim()) errs.clientName = 'Client name is required';
    }
    return errs;
  }, [form]);

  const handleNext = () => {
    const errs = validateStep(step);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setStep(s => s + 1);
  };

  const handleBack = () => setStep(s => s - 1);

  const addCoAgent = () => {
    if (form.coClosingAgents.length >= 3) return;
    setForm(prev => ({
      ...prev,
      coClosingAgents: [...prev.coClosingAgents, { leaderId: '', agentId: '' }]
    }));
  };

  const removeCoAgent = (idx) => {
    setForm(prev => ({
      ...prev,
      coClosingAgents: prev.coClosingAgents.filter((_, i) => i !== idx)
    }));
  };

  const setCoAgent = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      coClosingAgents: prev.coClosingAgents.map((c, i) => i === idx ? { ...c, [field]: value } : c)
    }));
  };

  const handleSubmit = async () => {
    const errs = validateStep(step);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const propertySizeText = form.propertySizeText.trim() || null;
    const parsedSize = parsePropertySizeText(propertySizeText);
    const propertySizeNum = propertySizeText
      ? (parsedSize || (parseFloat(propertySizeText) || null))
      : null;

    const payload = {
      projectNumber: form.projectNumber.trim(),
      depositAmount: form.depositAmount ? parseFloat(form.depositAmount) : null,
      depositReceivedDate: form.depositReceivedDate || null,

      clientName: form.clientName.trim(),
      clientIC: form.clientIC.trim() || null,
      clientPhone: form.clientPhone.trim() || null,
      clientEmail: form.clientEmail.trim() || null,

      addressLine1: form.addressLine1.trim() || null,
      addressLine2: form.addressLine2.trim() || null,
      postcode: form.postcode.trim() || null,
      city: form.city.trim() || null,
      state: form.state.trim() || null,
      propertyType: form.propertyType || null,
      propertySize: propertySizeNum,
      propertySizeText,

      finalPackagePrice: form.finalPackagePrice ? parseFloat(form.finalPackagePrice) : null,
      commissionAmount: form.commissionAmount ? parseFloat(form.commissionAmount) : null,
      contractSignedDate: form.contractSignedDate || null,
      signingVenue: form.signingVenue.trim() || null,
      lockPricePaymentAmount: form.lockPricePaymentAmount ? parseFloat(form.lockPricePaymentAmount) : null,
      lockPricePaymentDate: form.lockPricePaymentDate || null,
      lockPricePaymentMethod: form.lockPricePaymentMethod.trim() || null,
      lockPriceReferenceNumber: form.lockPriceReferenceNumber.trim() || null,

      managerId: form.managerId || null,
      leaderId: form.leaderId || null,
      agentId: form.agentId || null,
      coClosingAgents: form.coClosingAgents.filter(c => c.agentId),
      sidId: form.sidId || null,
      siteManagerId: form.siteManagerId || null,
    };

    setSubmitting(true);
    try {
      await siteDatabaseAPI.createManual(payload);
      toast.success('Site entry created successfully');
      onSuccess?.();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to create site entry';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const parsedArea = parsePropertySizeText(form.propertySizeText);

  // Summary helpers
  const managerName = managers.find(u => u.id === form.managerId)?.nickname || managers.find(u => u.id === form.managerId)?.fullName || '—';
  const leaderName = leaders.find(u => u.id === form.leaderId)?.nickname || leaders.find(u => u.id === form.leaderId)?.fullName || '—';
  const agentName = agents.find(u => u.id === form.agentId)?.nickname || agents.find(u => u.id === form.agentId)?.fullName || '—';
  const sidName = sidUsers.find(u => u.id === form.sidId)?.nickname || sidUsers.find(u => u.id === form.sidId)?.fullName || '—';
  const smName = siteManagers.find(u => u.id === form.siteManagerId)?.nickname || siteManagers.find(u => u.id === form.siteManagerId)?.fullName || '—';

  if (!isOpen) return null;

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Site Entry"
      size="lg"
      maxHeight="92vh"
    >
      <div className="flex flex-col min-h-0">
        {/* Step indicator */}
        <div className="px-4 pt-2 pb-4">
          <div className="flex items-center justify-between mb-1">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => {
              const num = i + 1;
              const active = num === step;
              const done = num < step;
              return (
                <React.Fragment key={num}>
                  <div className="flex flex-col items-center">
                    <button
                      type="button"
                      onClick={() => setStep(num)}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors
                        ${done ? 'bg-black border-black text-white hover:bg-gray-800' : active ? 'border-black text-black bg-white' : 'border-gray-300 text-gray-400 bg-white hover:border-gray-400'}`}
                    >
                      {done ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : num}
                    </button>
                  </div>
                  {num < TOTAL_STEPS && (
                    <div className={`flex-1 h-0.5 mx-1 ${done ? 'bg-black' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <div className="flex justify-between mt-1 text-xs text-gray-500">
            <span>Project</span>
            <span>Client</span>
            <span>Property</span>
            <span>Financial</span>
            <span>Team</span>
          </div>
        </div>

        {/* Step content */}
        <div className="px-4 pb-4 flex-1 overflow-y-auto">

          {/* ── Step 1: Project Details ── */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.projectNumber}
                  onChange={e => set('projectNumber', e.target.value)}
                  placeholder="e.g. 0042, TNQ-001"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm
                    ${errors.projectNumber ? 'border-red-400' : 'border-gray-300'}`}
                />
                {errors.projectNumber && <p className="mt-1 text-xs text-red-500">{errors.projectNumber}</p>}
                {projectNumberSuggestion && (
                  <p className="mt-1 text-xs text-gray-400">Suggestion: {projectNumberSuggestion}</p>
                )}
              </div>


              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deposit Amount (RM)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.depositAmount}
                    onChange={e => set('depositAmount', e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deposit Received Date</label>
                  <input
                    type="date"
                    value={form.depositReceivedDate}
                    onChange={e => set('depositReceivedDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── Step 2: Client Information ── */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.clientName}
                  onChange={e => set('clientName', e.target.value)}
                  placeholder="Full name"
                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm
                    ${errors.clientName ? 'border-red-400' : 'border-gray-300'}`}
                />
                {errors.clientName && <p className="mt-1 text-xs text-red-500">{errors.clientName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IC Number</label>
                <input
                  type="text"
                  value={form.clientIC}
                  onChange={e => set('clientIC', e.target.value)}
                  placeholder="e.g. 900101-01-1234"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={form.clientPhone}
                    onChange={e => set('clientPhone', e.target.value)}
                    placeholder="0123456789"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={form.clientEmail}
                    onChange={e => set('clientEmail', e.target.value)}
                    placeholder="client@email.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ── Step 3: Property Information ── */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                <input
                  type="text"
                  value={form.addressLine1}
                  onChange={e => set('addressLine1', e.target.value)}
                  placeholder="e.g. No. 12, Jalan ABC"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                <input
                  type="text"
                  value={form.addressLine2}
                  onChange={e => set('addressLine2', e.target.value)}
                  placeholder="e.g. Taman XYZ"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                  <input
                    type="text"
                    value={form.postcode}
                    onChange={e => set('postcode', e.target.value)}
                    placeholder="47810"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={e => set('city', e.target.value)}
                    placeholder="Petaling Jaya"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={form.state}
                    onChange={e => set('state', e.target.value)}
                    placeholder="Selangor"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property Type</label>
                <select
                  value={form.propertyType}
                  onChange={e => set('propertyType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                >
                  <option value="">-- Select --</option>
                  {PROPERTY_TYPES.map(pt => (
                    <option key={pt.value} value={pt.value}>{pt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Property Size</label>
                <input
                  type="text"
                  value={form.propertySizeText}
                  onChange={e => set('propertySizeText', e.target.value)}
                  placeholder="e.g. 1400 or 20 x 70"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                />
                {parsedArea && (
                  <p className="mt-1 text-xs text-gray-500">= {parsedArea.toLocaleString()} sqft</p>
                )}
              </div>
            </div>
          )}

          {/* ── Step 4: Financial Details ── */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Final Package Price (RM)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.finalPackagePrice}
                    onChange={e => set('finalPackagePrice', e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Commission Amount (RM)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.commissionAmount}
                    onChange={e => set('commissionAmount', e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contract Signed Date</label>
                  <input
                    type="date"
                    value={form.contractSignedDate}
                    onChange={e => set('contractSignedDate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Signing Venue</label>
                  <input
                    type="text"
                    value={form.signingVenue}
                    onChange={e => set('signingVenue', e.target.value)}
                    placeholder="e.g. HQ Office"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                  />
                </div>
              </div>

              {/* Lock Price Details — collapsible */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setLockPriceOpen(o => !o)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <span>Lock Price Details <span className="font-normal text-gray-400">(optional)</span></span>
                  <svg className={`w-4 h-4 text-gray-500 transition-transform ${lockPriceOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {lockPriceOpen && (
                  <div className="p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Lock Price Amount (RM)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.lockPricePaymentAmount}
                          onChange={e => set('lockPricePaymentAmount', e.target.value)}
                          placeholder="0.00"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                        <input
                          type="date"
                          value={form.lockPricePaymentDate}
                          onChange={e => set('lockPricePaymentDate', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                        <select
                          value={form.lockPricePaymentMethod}
                          onChange={e => set('lockPricePaymentMethod', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm bg-white"
                        >
                          <option value="">— Select —</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="cash">Cash</option>
                          <option value="cheque">Cheque</option>
                          <option value="credit_card">Credit Card</option>
                          <option value="online_banking">Online Banking</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                        <input
                          type="text"
                          value={form.lockPriceReferenceNumber}
                          onChange={e => set('lockPriceReferenceNumber', e.target.value)}
                          placeholder="e.g. TXN001"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-400">
                Payment milestones (Lock Price, Site Commencement, Progress, Final Completion) will be auto-created based on the package price.
              </p>
            </div>
          )}

          {/* ── Step 5: Team Assignment & Review ── */}
          {step === 5 && (
            <div className="space-y-5">
              {/* Primary Sales Team */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Primary Sales Team</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Manager</label>
                    <select
                      value={form.managerId}
                      onChange={e => set('managerId', e.target.value)}
                      disabled={loadingUsers}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm disabled:bg-gray-100"
                    >
                      <option value="">— None —</option>
                      {managers.map(u => (
                        <option key={u.id} value={u.id}>{u.nickname || u.fullName}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Leader</label>
                    <select
                      value={form.leaderId}
                      onChange={e => set('leaderId', e.target.value)}
                      disabled={loadingUsers}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm disabled:bg-gray-100"
                    >
                      <option value="">— None —</option>
                      {leaders.map(u => (
                        <option key={u.id} value={u.id}>{u.nickname || u.fullName}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Agent</label>
                    <select
                      value={form.agentId}
                      onChange={e => set('agentId', e.target.value)}
                      disabled={loadingUsers}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm disabled:bg-gray-100"
                    >
                      <option value="">— None —</option>
                      {agents.map(u => (
                        <option key={u.id} value={u.id}>{u.nickname || u.fullName}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Co-Closing Agents */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-800">Co-Closing Agents <span className="font-normal text-gray-400">(optional)</span></h4>
                  {form.coClosingAgents.length < 3 && (
                    <button
                      type="button"
                      onClick={addCoAgent}
                      className="text-xs text-black font-medium hover:underline"
                    >
                      + Add Co-Agent
                    </button>
                  )}
                </div>
                {!form.managerId && (
                  <p className="text-xs text-gray-400 mb-2">Select a Manager in Primary Sales Team to filter co-closing options.</p>
                )}
                {form.coClosingAgents.length === 0 && (
                  <p className="text-xs text-gray-400">No co-closing agents added.</p>
                )}
                {form.coClosingAgents.map((co, idx) => {
                  const coLeaders = form.managerId
                    ? leaders.filter(u => u.managerId === form.managerId)
                    : leaders;
                  const coAgents = co.leaderId
                    ? agents.filter(u => u.leaderId === co.leaderId)
                    : form.managerId
                      ? agents.filter(u => coLeaders.some(l => l.id === u.leaderId))
                      : agents;
                  return (
                    <div key={idx} className="flex gap-2 items-start mb-2">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <select
                          value={co.leaderId}
                          onChange={e => setCoAgent(idx, 'leaderId', e.target.value)}
                          disabled={loadingUsers}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-100"
                        >
                          <option value="">— Leader (opt.) —</option>
                          {coLeaders.map(u => (
                            <option key={u.id} value={u.id}>{u.nickname || u.fullName}</option>
                          ))}
                        </select>
                        <select
                          value={co.agentId}
                          onChange={e => setCoAgent(idx, 'agentId', e.target.value)}
                          disabled={loadingUsers}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-1 focus:ring-black disabled:bg-gray-100"
                        >
                          <option value="">— Agent —</option>
                          {coAgents.map(u => (
                            <option key={u.id} value={u.id}>{u.nickname || u.fullName}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeCoAgent(idx)}
                        className="mt-1 p-1.5 text-gray-400 hover:text-red-500"
                        title="Remove"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* SID / ASID */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-2">SID / ASID</h4>
                <select
                  value={form.sidId}
                  onChange={e => set('sidId', e.target.value)}
                  disabled={loadingUsers}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm disabled:bg-gray-100"
                >
                  <option value="">— None —</option>
                  {(() => {
                    const sids = sidUsers.filter(u => u.role === 'sid');
                    const asids = sidUsers.filter(u => u.role === 'asid');
                    const ungroupedAsids = asids.filter(a => !sids.find(s => s.id === a.sidId));
                    return (
                      <>
                        {sids.map(sid => (
                          <optgroup key={sid.id} label={`${sid.nickname || sid.fullName} (SID)`}>
                            <option value={sid.id}>{sid.nickname || sid.fullName}</option>
                            {asids.filter(a => a.sidId === sid.id).map(asid => (
                              <option key={asid.id} value={asid.id}>&nbsp;&nbsp;{asid.nickname || asid.fullName} (ASID)</option>
                            ))}
                          </optgroup>
                        ))}
                        {ungroupedAsids.length > 0 && (
                          <optgroup label="ASID (no SID assigned)">
                            {ungroupedAsids.map(asid => (
                              <option key={asid.id} value={asid.id}>{asid.nickname || asid.fullName} (ASID)</option>
                            ))}
                          </optgroup>
                        )}
                      </>
                    );
                  })()}
                </select>
              </div>

              {/* Site Manager */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-1">Site Manager</h4>
                <select
                  value={form.siteManagerId}
                  onChange={e => set('siteManagerId', e.target.value)}
                  disabled={loadingUsers}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black text-sm disabled:bg-gray-100"
                >
                  <option value="">— None —</option>
                  {siteManagers.map(u => (
                    <option key={u.id} value={u.id}>{u.nickname || u.fullName}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-400">Site Supervisor can be assigned after creation.</p>
              </div>

              {/* Review Summary */}
              <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3 text-sm">
                <p className="font-semibold text-gray-700">Review</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <span className="text-gray-500">Project No.</span><span className="font-medium">{form.projectNumber || '—'}</span>
                  <span className="text-gray-500">Client</span><span className="font-medium">{form.clientName || '—'}</span>
                  <span className="text-gray-500">Phone</span><span className="font-medium">{form.clientPhone || '—'}</span>
                  <span className="text-gray-500">Address</span><span className="font-medium">{[form.addressLine1, form.city, form.state].filter(Boolean).join(', ') || '—'}</span>
                  <span className="text-gray-500">Property</span><span className="font-medium">{PROPERTY_TYPES.find(p => p.value === form.propertyType)?.label || '—'}</span>
                  <span className="text-gray-500">Package Price</span>
                  <span className="font-medium">{form.finalPackagePrice ? `RM ${parseFloat(form.finalPackagePrice).toLocaleString()}` : '—'}</span>
                  <span className="text-gray-500">Deposit</span>
                  <span className="font-medium">{form.depositAmount ? `RM ${parseFloat(form.depositAmount).toLocaleString()}` : '—'}</span>
                  <span className="text-gray-500">Contract Date</span><span className="font-medium">{form.contractSignedDate || '—'}</span>
                  <span className="text-gray-500">Manager</span><span className="font-medium">{form.managerId ? managerName : '—'}</span>
                  <span className="text-gray-500">Leader</span><span className="font-medium">{form.leaderId ? leaderName : '—'}</span>
                  <span className="text-gray-500">Agent</span><span className="font-medium">{form.agentId ? agentName : '—'}</span>
                  <span className="text-gray-500">SID / ASID</span><span className="font-medium">{form.sidId ? sidName : '—'}</span>
                  <span className="text-gray-500">Site Manager</span><span className="font-medium">{form.siteManagerId ? smName : '—'}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-200 flex justify-between items-center gap-3 bg-white">
          <button
            type="button"
            onClick={step === 1 ? onClose : handleBack}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          {step < TOTAL_STEPS ? (
            <button
              type="button"
              onClick={handleNext}
              className="px-5 py-2 text-sm font-medium text-white bg-black rounded-md hover:bg-gray-800"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="px-5 py-2 text-sm font-medium text-white bg-black rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {submitting && (
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              {submitting ? 'Creating...' : 'Create Site Entry'}
            </button>
          )}
        </div>
      </div>
    </ResponsiveModal>
  );
}
